import { Box, IconButton, useMediaQuery } from "@mui/material";
import { makeStyles } from '@mui/styles';
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { UserContext } from "../../../contexts/user.context";
import { DashboardContext } from "../../../contexts/dashboard.context.jsx";
import AlertCollapse from "../../customComponents/alert.collapse";
import DraggableContainer from "../../customComponents/draggable.container";
import OwnerRoomButtonBar from "./owner.room.button.bar";
import OwnerRoomTable from "./owner.room.table";
import DashboardNotification from "../dashboard/owner.dashboard.notification.jsx";
import DeliveryNotification from "../delivery/deliveryNotification.jsx";
import socketIOClient from "socket.io-client";
import ComponentLoader from "../../customComponents/component.loader.jsx";
import Notification from "../../customComponents/notification.jsx";
import DialogMenuMobile from "./owner.room.dialog.menu.mobile";
import DialogMenu from "./owner.room.dialog.menu";
import RoomDialogView from "./owner.room.dialog.view";

const canvasSize = 2000;

const useStyles = makeStyles((theme) => ({
  canvas: {
    width: "100%",
    height: "85vh",
    border: `2px dashed ${theme.palette.background.paper}`,
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    overflow: 'auto',
    boxSizing: 'border-box',
    "&::-webkit-scrollbar": { width: "10px", height: 8 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "#43a047" },
  },
  canvasWrapper: {
    padding: "0 1%",
    width: "100%",
    boxSizing: 'border-box'
  },
  addButton: {
    top: '1px',
    left: '5px',
    backgroundColor: '#43a047 !important',
    position: 'absolute !important',
    zIndex: 100
  },
}));

export default function OwnerRoom() {
  const classes = useStyles();
  const canvasRef = useRef();

  // STATI LOCALI
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Inizia true per caricare le sale
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [isLocked, setIsLocked] = useState(true);
  const [isClickedOut, setIsClickedOut] = useState(false);

  const isMobile = useMediaQuery("(max-width:576px)");
  const { user } = useContext(UserContext);
  const { isLoading: isLoadingDash, notification, setNotification, dataMenuObj, data } = useContext(DashboardContext);
  const [socket, setSocket] = useState(null);

  const [selectedTable, setSelectedTable] = useState(null);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  // Socket setup
  useEffect(() => {
    if (user?.id) {
      const s = socketIOClient(process.env.REACT_APP_SERVER_URL, {
        secure: true,
        transports: ["websocket"],
        query: { ownerId: user.id },
      });
      setSocket(s);
      return () => s.disconnect();
    }
  }, [user]);

  // ----------------------------------------------------
  // 1. NUOVO: Caricamento centralizzato delle SALE
  // ----------------------------------------------------
  useEffect(() => {
    let mounted = true;
    const fetchRooms = async () => {
      setIsLoading(true);
      try {
        const { data } = await axios.post('/api/rooms/owner/get');
        if (mounted && data?.success) {
          setRooms(data.roomsList || []);

          // Se abbiamo sale e nessuna è selezionata, seleziona la prima
          if (data.roomsList?.length > 0) {
            setCurrentRoom(data.roomsList[0].id);
          }
        }
      } catch (err) {
        console.error("Errore fetch rooms:", err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchRooms();
    return () => { mounted = false; };
  }, []);

  // ----------------------------------------------------
  // 2. Caricamento dei TAVOLI (Dipende da currentRoom e socket)
  // ----------------------------------------------------
  const fetchTables = useCallback(async () => {
    // Non caricare se non abbiamo ancora una stanza o il socket
    if (!currentRoom || !socket) return;

    setIsLocalLoading(true);
    setError(null);

    try {
      const { data } = await axios.post(
        "/api/rooms/owner/tables/get",
        { id: currentRoom, socketId: socket.id }
      );

      if (data?.success) {
        setTables(data.tablesList || []);
      } else {
        setError(true);
      }
    } catch (err) {
      setError(true);
    } finally {
      setIsLocalLoading(false);
    }
  }, [currentRoom, socket]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // Gestione Coordinate
  const setCoords = useCallback((tableId, x, y, businessId) => {
    setTables(prev => {
      const index = prev.findIndex(t => t.id === tableId);
      if (index === -1) return prev;
      const newTables = [...prev];
      newTables[index] = { ...newTables[index], x, y };
      return newTables;
    });

    axios.post("/api/tables/owner/setCoords", { tableId, x, y, businessId });
  }, []);

  const deleteTable = (tableId, businessId) => {
    axios.post("/api/tables/owner/delete", { id: tableId, businessId }).then(({ data }) => {
      if (data?.success) setTables(prev => prev.filter(t => t.id !== tableId));
    });
  };

  const createTable = () => {
    const newTable = { x: 50, y: 50, roomId: currentRoom };
    axios.post("/api/tables/owner/add", newTable).then(({ data }) => {
      if (data?.success) {
        setTables(prev => [...prev, { ...newTable, id: data.id, businessId: data.businessId }]);
      }
    });
  };

  // Status dei tavoli basato sui dati del DashboardContext
  const processedTables = useMemo(() => {
    const safeData = Array.isArray(data) ? data : [];
    const busyTableIds = new Set(safeData.map(order => order.tableId));

    return tables.map(t => ({
      ...t,
      status: busyTableIds.has(t.id) ? 1 : 0
    }));
  }, [tables, data]);

  const handleOpenOrder = useCallback((table) => {
    setSelectedTable(table);
    setIsOrderDialogOpen(true);
  }, []);

  const handleOpenView = useCallback((table) => {
    setSelectedTable(table);
    setIsViewDialogOpen(true);
  }, []);

  return (
    <div id="onwer_room">
      <Notification setStatus={setNotification} status={notification} bottom />

      <ComponentLoader isLoading={isLoadingDash || isLoading}>
        <OwnerRoomButtonBar
          rooms={rooms}
          setRooms={setRooms}
          setCurrentRoom={setCurrentRoom}
          currentRoom={currentRoom}
          setTables={setTables}
          isLocked={isLocked}
          setIsLocked={setIsLocked}
        />

        <AlertCollapse text="Errore nel caricamento dei tavoli" show={error} />

        {/* Loader specifico per il cambio sala */}
        <ComponentLoader isLoading={isLocalLoading}>

          {/* DIALOGS */}
          {isOrderDialogOpen && selectedTable && (
            isMobile ? (
              <DialogMenuMobile
                openDialog={isOrderDialogOpen} setOpenDialog={setIsOrderDialogOpen}
                table={selectedTable} dataMenuObj={dataMenuObj}
                handleClickOrder={() => setIsOrderDialogOpen(false)} ownerId={user.id}
              />
            ) : (
              <DialogMenu
                tableId={selectedTable.id} openDialog={isOrderDialogOpen}
                setOpenDialog={setIsOrderDialogOpen} dataMenuObj={dataMenuObj}
                handleClickOrder={() => setIsOrderDialogOpen(false)} ownerId={user.id}
              />
            )
          )}

          {isViewDialogOpen && selectedTable && (
            <RoomDialogView
              openDialogView={isViewDialogOpen} setOpenDialogView={setIsViewDialogOpen}
              tableId={selectedTable.id} businessId={selectedTable.businessId}
            />
          )}

          <Box sx={{ width: '100%', padding: '0 1%', boxSizing: 'border-box' }}>
            {!isMobile ? (
              /* LATO DESKTOP */
              <DraggableContainer className={classes.canvas} size={canvasSize} ref={canvasRef}>
                {processedTables.map((table) => (
                  <OwnerRoomTable
                    key={table.id}
                    canvasRef={canvasRef}
                    table={table}
                    setCoords={setCoords}
                    isLocked={isLocked}
                    onOpenOrder={handleOpenOrder}
                    onOpenView={handleOpenView}
                    deleteTable={deleteTable}
                  />
                ))}

                {!isLocked && currentRoom && ( // Mostra tasto + solo se c'è una stanza
                  <IconButton onClick={createTable} className={classes.addButton}>
                    <AddIcon />
                  </IconButton>
                )}
              </DraggableContainer>
            ) : (
              /* LATO MOBILE */
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 2,
                  justifyContent: 'center',
                  marginBottom: '150px'
                }}
              >
                {processedTables.map((table) => (
                  <Box key={table.id} sx={{ flex: '0 0 auto' }}>
                    <OwnerRoomTable
                      table={table}
                      isLocked={isLocked}
                      onOpenOrder={handleOpenOrder}
                      onOpenView={handleOpenView}
                      isMobile={true}
                      deleteTable={deleteTable}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {(user.version === 1 || user.version === 3) && <DashboardNotification isClickedOut={isClickedOut} setIsClickedOut={setIsClickedOut} />}
          {(user.version === 2 || user.version === 3) && <DeliveryNotification />}

        </ComponentLoader>
      </ComponentLoader>
    </div>
  );
}